/**
 * InvoiceShareModal — shared by tenant invoices (/app/invoices) and platform
 * invoices (/admin/accounting → Invoices tab).
 *
 * Issues a short-lived public link, then offers 4 actions:
 *   View · Download PDF · Email (mailto) · WhatsApp (wa.me)
 *
 * Each "send" action opens the device's native picker pre-filled with the
 * configured template — NO server-side send required.
 */
import { useEffect, useState } from "react";
import { Mail, MessageCircle, Download, ExternalLink, Copy, CheckCircle2, RefreshCw } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import Modal, { GoldButton, GhostButton } from "@/components/Ui";
import { fmtDate } from "@/lib/time";

const SCOPE_ENDPOINT = {
    tenant:   (id) => `/invoices/${id}/share-link`,
    platform: (id) => `/admin/accounting/invoices/${id}/share-link`,
};

export default function InvoiceShareModal({ scope, invoice, open, onClose }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [copied, setCopied] = useState(false);
    const invoiceId = invoice?.id;

    useEffect(() => {
        if (!open || !invoiceId) { setData(null); return; }
        setLoading(true);
        api.post(SCOPE_ENDPOINT[scope](invoiceId), { ttl_days: 30 })
            .then(r => setData(r.data))
            .catch(e => { toast.error(apiError(e)); onClose?.(); })
            .finally(() => setLoading(false));
    }, [open, invoiceId, scope, onClose]);

    if (!open) return null;

    const link = data?.link;
    const num = scope === "tenant" ? (invoice?.number) : (invoice?.invoice_number || invoice?.number);

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(link);
            setCopied(true); setTimeout(() => setCopied(false), 1800);
        } catch {
            toast.error("Could not copy");
        }
    };

    const openEmail = () => {
        const subject = encodeURIComponent(data?.email_subject || `Invoice ${num}`);
        const body = encodeURIComponent(data?.email_body || link);
        // Prefer client email if known; otherwise let user pick a recipient
        const to = data?.context?.client_email || invoice?.client_email || "";
        window.location.href = `mailto:${to}?subject=${subject}&body=${body}`;
    };

    const openWhatsApp = () => {
        const text = encodeURIComponent(data?.whatsapp_text || `${link}`);
        // No phone hint — opens the user's contact picker in WhatsApp
        window.open(`https://wa.me/?text=${text}`, "_blank", "noopener");
    };

    const refresh = () => {
        setLoading(true);
        api.post(SCOPE_ENDPOINT[scope](invoiceId), { ttl_days: 30 })
            .then(r => { setData(r.data); toast.success("New link generated"); })
            .catch(e => toast.error(apiError(e)))
            .finally(() => setLoading(false));
    };

    return (
        <Modal open={open} onClose={onClose} size="lg" title={`Share invoice — ${num || ""}`} testId="invoice-share-modal">
            {loading && !data ? (
                <div className="text-center py-12 text-muted-foreground">
                    <RefreshCw className="w-5 h-5 animate-spin mx-auto mb-3 text-primary"/>
                    Generating secure link…
                </div>
            ) : data ? (
                <div className="space-y-5">
                    {/* Link strip */}
                    <div className="bg-background border border-border p-3">
                        <div className="overline mb-2 text-[10px]">Public link · expires {fmtDate(data.expires_at)}</div>
                        <div className="flex flex-wrap items-center gap-2">
                            <a href={link} target="_blank" rel="noreferrer noopener" className="flex-1 min-w-[180px] text-[11px] font-mono text-primary hover:underline break-all" data-testid="share-link-url">{link}</a>
                            <button onClick={copy} data-testid="share-link-copy" className="px-3 py-1.5 border border-border hover:border-primary text-[10px] uppercase tracking-[0.16em] inline-flex items-center gap-1.5">
                                {copied ? <><CheckCircle2 className="w-3 h-3 text-emerald-400"/>Copied</> : <><Copy className="w-3 h-3"/>Copy</>}
                            </button>
                            <button onClick={refresh} disabled={loading} data-testid="share-link-refresh" title="Generate a new link" className="px-3 py-1.5 border border-border hover:border-primary text-[10px] uppercase tracking-[0.16em] inline-flex items-center gap-1.5 disabled:opacity-50">
                                <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`}/>New
                            </button>
                        </div>
                    </div>

                    {/* 4 action grid */}
                    <div className="grid grid-cols-2 gap-3">
                        <a href={link} target="_blank" rel="noreferrer noopener" data-testid="share-action-open"
                            className="border border-border hover:border-primary p-4 text-center group">
                            <ExternalLink className="w-5 h-5 mx-auto mb-2 text-primary group-hover:scale-110 transition-transform"/>
                            <div className="text-xs">Open invoice</div>
                            <div className="text-[10px] text-muted-foreground mt-0.5">Preview in browser</div>
                        </a>
                        <a href={`${link}/pdf`} target="_blank" rel="noreferrer noopener" download data-testid="share-action-download"
                            className="border border-border hover:border-primary p-4 text-center group">
                            <Download className="w-5 h-5 mx-auto mb-2 text-primary group-hover:scale-110 transition-transform"/>
                            <div className="text-xs">Download PDF</div>
                            <div className="text-[10px] text-muted-foreground mt-0.5">Save to device</div>
                        </a>
                        <button onClick={openEmail} data-testid="share-action-email"
                            className="border border-border hover:border-primary p-4 text-center group">
                            <Mail className="w-5 h-5 mx-auto mb-2 text-primary group-hover:scale-110 transition-transform"/>
                            <div className="text-xs">Email to client</div>
                            <div className="text-[10px] text-muted-foreground mt-0.5">Opens mail app</div>
                        </button>
                        <button onClick={openWhatsApp} data-testid="share-action-whatsapp"
                            className="border border-border hover:border-primary p-4 text-center group">
                            <MessageCircle className="w-5 h-5 mx-auto mb-2 text-primary group-hover:scale-110 transition-transform"/>
                            <div className="text-xs">WhatsApp client</div>
                            <div className="text-[10px] text-muted-foreground mt-0.5">Opens WhatsApp</div>
                        </button>
                    </div>

                    {/* Template previews */}
                    <details className="border border-border bg-background">
                        <summary className="px-3 py-2 text-[10px] uppercase tracking-[0.16em] text-muted-foreground cursor-pointer hover:text-foreground">
                            Preview email & WhatsApp text · Edit templates in {scope === "tenant" ? "Settings → Share templates" : "Super-admin → Share templates"}
                        </summary>
                        <div className="p-3 space-y-3 text-xs">
                            <div>
                                <div className="overline mb-1">Email subject</div>
                                <div className="text-foreground font-mono break-words">{data.email_subject}</div>
                            </div>
                            <div>
                                <div className="overline mb-1">Email body</div>
                                <pre className="whitespace-pre-wrap text-muted-foreground font-sans">{data.email_body}</pre>
                            </div>
                            <div>
                                <div className="overline mb-1">WhatsApp text</div>
                                <pre className="whitespace-pre-wrap text-muted-foreground font-sans">{data.whatsapp_text}</pre>
                            </div>
                        </div>
                    </details>

                    <div className="flex justify-end pt-2">
                        <GoldButton onClick={onClose} data-testid="share-done-btn">Done</GoldButton>
                    </div>
                </div>
            ) : null}
        </Modal>
    );
}
